import {
  Box,
  Card,
  DismissableLayer,
  Flex,
  FlexItem,
  Slot,
  Spinner
} from '@volue/wave-react';
import { geoJSON } from 'leaflet';
import type { PropsWithChildren } from 'react';
import React from 'react';
import { useMap } from 'react-leaflet';

import { useActions, useAppState } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

type DismissableCardProps = { onDismiss: () => void } & PropsWithChildren;

function DismissableCard({ onDismiss, children }: DismissableCardProps) {
  return (
    <DismissableLayer as={Slot} onDismiss={onDismiss}>
      <Card
        css={{
          padding: 0,
          maxHeight: 'calc(100% - 3rem)'
        }}
      >
        {children}
      </Card>
    </DismissableLayer>
  );
}

export function FeatureSearchResult() {
  const searchState = useAppState().arcgis.featureSearch;
  const subtypes = useAppState().arcgis.featureTypes.value ?? {};

  const { setSelectedFeature } = useActions().selectedFeature;
  const clearSearchResults =
    useActions().arcgis.featureSearch.resetFeatureSearchState;
  const mapRef = useMap();

  const centerMapOnFeature = (feature: FeatureWithIDs) => {
    if (!mapRef) {
      return;
    }
    const featureBounds = geoJSON(feature).getBounds();
    mapRef.fitBounds(featureBounds, { animate: false });
  };

  const selectFeature = (feature: FeatureWithIDs) => {
    centerMapOnFeature(feature);
    clearSearchResults();
    setSelectedFeature({ ...feature });
  };

  if (searchState.current === 'LOADING') {
    return (
      <Card css={{ paddingInline: '$spacingM' }}>
        <Flex main="center">
          <Spinner />
        </Flex>
      </Card>
    );
  }
  if (searchState.current === 'ERROR') {
    return (
      <DismissableCard onDismiss={() => clearSearchResults()}>
        <Box css={{ padding: '$spacingM' }}>
          {searchState.error === 'NO_AVAILABLE_RULES'
            ? 'Search is not configured'
            : 'Search failed'}
        </Box>
      </DismissableCard>
    );
  }
  if (searchState.current !== 'LOADED') {
    return null;
  }

  const numberOfResults = searchState.value.flatMap(
    layer => layer.features
  ).length;

  if (numberOfResults === 0) {
    return (
      <DismissableCard onDismiss={() => clearSearchResults()}>
        <Box css={{ padding: '$spacingM' }}>
          {'No matching grid elements found'}
        </Box>
      </DismissableCard>
    );
  }

  return (
    <DismissableCard onDismiss={() => clearSearchResults()}>
      <Flex
        flow="column"
        gap="none"
        css={{ overflow: 'auto', padding: '$spacingL' }}
      >
        {searchState.value.map(searchResult =>
          searchResult.features.map(feature => (
            <Flex
              key={feature.gc.globalId}
              onClick={() => selectFeature(feature)}
              css={{
                padding: '$spacingS',
                '&:hover': {
                  background: '$colorAccent5',
                  borderRadius: '0.25rem'
                }
              }}
            >
              <FlexItem grow={1}>
                {feature.properties?.[searchResult.attributeField] ??
                  feature.gc.title}
                <Box css={{ color: '$colorGray25' }}>
                  {subtypes[feature.gc.type as number]}
                </Box>
              </FlexItem>
            </Flex>
          ))
        )}
      </Flex>
    </DismissableCard>
  );
}
